/**
 * Animal Health Certificate (AHC) questionnaire — PDF component, field types,
 * and shared constants.
 *
 * This is a multi-page questionnaire that collects travel, owner, and pet
 * details needed to issue an AHC for EU travel.
 *
 * Auto-filled context: clinic header, patient name/breed/microchip/DOB, owner details.
 * Manual fields: everything else — travel plans, confirmations, rabies status.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import {
  formatCalendarDate,
  resolveDateFormat,
} from '@/frontend/features/printing/documents/blocks/format-utils'
import { OwnerSignature } from '@/frontend/features/printing/documents/blocks/owner-signature'
import {
  CheckboxField,
  FillField,
  RadioField,
  Spacer,
  WithInlineResponse,
} from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

export interface AhcQuestionnaireFields {
  fullName: string
  petNames: string
  emailAddress: string
  travellingPerson: 'owner' | 'authorised' | ''
  authorisedRelationship: string
  ownerJoiningWithin5Days: 'yes' | 'no' | ''
  destination: string
  firstEuCountry: string
  travelMethod: 'ferry' | 'eurotunnel' | 'flight' | 'pet-courier' | 'other' | ''
  travelMethodOther: string
  departureDate: Date | null
  returnType: 'date' | 'indefinite' | ''
  returnDate: Date | null
  ownerFullName: string
  ownerAddress: string
  ownerPostcode: string
  ownerPhone: string
  petCount: string
  petName: string
  petSex: 'male' | 'male-neutered' | 'female' | 'female-neutered' | ''
  petColour: string
  petBreed: string
  microchipNumber: string
  dateOfBirth: string
  rabiesVaccination: 'yes-snoots' | 'yes-other' | 'no' | ''
  declarationAccepted: boolean
}

export const defaultAhcQuestionnaireFields: AhcQuestionnaireFields = {
  fullName: '',
  petNames: '',
  emailAddress: '',
  travellingPerson: '',
  authorisedRelationship: '',
  ownerJoiningWithin5Days: '',
  destination: '',
  firstEuCountry: '',
  travelMethod: '',
  travelMethodOther: '',
  departureDate: null,
  returnType: '',
  returnDate: null,
  ownerFullName: '',
  ownerAddress: '',
  ownerPostcode: '',
  ownerPhone: '',
  petCount: '',
  petName: '',
  petSex: '',
  petColour: '',
  petBreed: '',
  microchipNumber: '',
  dateOfBirth: '',
  rabiesVaccination: '',
  declarationAccepted: false,
}

export interface EuCountry {
  code: string
  label: string
  tapeworm?: boolean
}

export const EU_COUNTRIES: EuCountry[] = [
  { code: 'AT', label: 'Austria' },
  { code: 'BE', label: 'Belgium' },
  { code: 'BG', label: 'Bulgaria' },
  { code: 'HR', label: 'Croatia' },
  { code: 'CY', label: 'Cyprus' },
  { code: 'CZ', label: 'Czech Republic' },
  { code: 'DA', label: 'Denmark' },
  { code: 'EE', label: 'Estonia' },
  { code: 'FI', label: 'Finland', tapeworm: true },
  { code: 'FR', label: 'France' },
  { code: 'DE', label: 'Germany' },
  { code: 'EL', label: 'Greece' },
  { code: 'HU', label: 'Hungary' },
  { code: 'IE', label: 'Ireland', tapeworm: true },
  { code: 'IT', label: 'Italy' },
  { code: 'LV', label: 'Latvia' },
  { code: 'LT', label: 'Lithuania' },
  { code: 'LU', label: 'Luxembourg' },
  { code: 'MT', label: 'Malta', tapeworm: true },
  { code: 'NL', label: 'The Netherlands' },
  { code: 'PL', label: 'Poland' },
  { code: 'PT', label: 'Portugal' },
  { code: 'RO', label: 'Romania' },
  { code: 'SK', label: 'Slovakia' },
  { code: 'SI', label: 'Slovenia' },
  { code: 'ES', label: 'Spain' },
  { code: 'SE', label: 'Sweden' },
  { code: 'CH', label: 'Switzerland' },
  { code: 'NI', label: 'Northern Ireland', tapeworm: true },
  { code: 'NO', label: 'Norway', tapeworm: true },
]

/** Format an EU country for display, appending tapeworm note where required. */
export function formatCountryLabel(country: EuCountry): string {
  return country.tapeworm
    ? `${country.label} (${country.code})  (tapeworm treatment required)`
    : `${country.label} (${country.code})`
}

export const TRAVEL_METHODS = [
  { value: 'ferry', label: 'Ferry' },
  { value: 'eurotunnel', label: 'Eurotunnel' },
  { value: 'flight', label: 'Flight' },
  { value: 'pet-courier', label: 'Pet Courier Service' },
  { value: 'other', label: 'Other' },
] as const

export const PET_SEX_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'male-neutered', label: 'Male neutered' },
  { value: 'female', label: 'Female' },
  { value: 'female-neutered', label: 'Female neutered' },
] as const

export const PET_COUNT_OPTIONS = ['1', '2', '3', '4', '5'] as const

export const RABIES_OPTIONS = [
  { value: 'yes-snoots', label: 'Yes at Snoots' },
  { value: 'yes-other', label: 'Yes at another clinic, and I have proof of vaccination*.' },
  { value: 'no', label: 'No but I have/ will book in a rabies vaccination appointment asap' },
] as const

export const INTRO_TEXT = [
  'The person travelling with the pet must attend the appointment.',
  'The AHC must be issued no more than 10 days before departure.',
]

export const AUTHORISED_PERSON_NOTE =
  "Please note an authorisation letter signed by the pet owner must be presented at the time of the AHC appointment, and it is advised that the responsible person travelling with the animal carries the owner's authorisation letter in their hand luggage in case this proof is further required during documentary checks."

export const OWNER_NOT_JOINING_NOTE =
  '*If the owner is not joining this trip within 5 days, then the authorised person must be present for the AHC appointment to sign the declaration.'

export const FIRST_EU_COUNTRY_INFO =
  'First EU country you will enter on your trip? Please enter the first country of entry into EU e.g. if taking the ferry to Calais, put "France". You can find the official list of ports of entry here: https://ec.europa.eu/food/animals/movement-pets/travellers-points-entry_en AHCs are also accepted in Norway, Northern Ireland and Switzerland.'

export const FIRST_EU_COUNTRY_INFO_ITALIC =
  "On arrival in the EU and NI, pet owners travelling with their pets are required to enter through a designated Travellers' Point of Entry (TPE) where they may be asked to present proof of microchip and rabies vaccination alongside their pet's AHC and proof of tapeworm treatment (if required)."

export const AHC_VALIDITY_TEXTS = [
  'The AHC is valid for four months of onward movement between EU member states or until the date of expiry of the validity of the anti-rabies vaccination (whichever is earlier). During this time it can be used to re-enter GB.',
  'If your pet remains in the EU with its owner for more than four months or requires a rabies booster vaccination whilst in the EU, it will need a new certificate to return to GB. This can be either an EU pet passport or a Great Britain Pet Health Certificate issued by a vet in the EU.',
  'A new AHC is needed for each trip to the EU or NI.',
]

export const TAPEWORM_COUNTRIES_NOTE =
  'NB Dogs travelling from GB to tapeworm-free countries including Northern Ireland, Malta, Norway, Ireland and Finland must be treated for tapeworm prior to travel.'

export const PETS_NOTE =
  'Please note that AHCs can only be issued for domestic dogs, cats and ferrets.'

export const RABIES_PROOF_NOTE =
  "*If the rabies vaccination wasn't done at Snoots, please ensure you provide us with proof of vaccination (this can be a scan or a clear photograph of your pet's vaccination records, or ask your previous veterinary practice to email over your pet's medical history/ rabies certificate). This must confirm the date of microchip reading or implantation, as well as the date of rabies vaccination and batch numbers."

export const RABIES_NOT_VACCINATED_NOTE =
  'If your pet has not been vaccinated for rabies before, please book this in as soon as possible. The soonest we can issue an AHC is 21 days after your pet has received their rabies vaccination.'

export const DECLARATION_TEXT =
  'I declare that I have answered this questionnaire truthfully and that I will send proof of rabies vaccination if not administered at a Snoots clinic to team@snootsvet.com within 48 hours of completing this form. I understand that if I have entered any information incorrectly on this form, this may invalidate the AHC and I may have to rearrange my trip.'

/** Format a Date as "dd/MM/yyyy" for travel date display. */
export function formatDateNumeric(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/London',
  })
}

function SectionDivider() {
  return (
    <View
      style={{
        borderBottomWidth: 0.5,
        borderBottomColor: '#ccc',
        marginVertical: mm(6),
      }}
    />
  )
}

const styles = StyleSheet.create({
  ...baseStyles,
  boldItalic: { fontFamily: 'Helvetica-BoldOblique' },
  sectionTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    textDecoration: 'underline',
    marginBottom: mm(4),
  },
  indent: { paddingLeft: mm(6) },
})

export function AhcQuestionnairePdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: AhcQuestionnaireFields
}) {
  const { patient, owner, clinic } = context
  const onFileMicrochip = patient.microchipNumber
  // Generated output with no viewer to follow — resolve from the owning member's region.
  const fmt = resolveDateFormat(null, owner.region)
  const onFileDob = patient.dateOfBirth ? formatCalendarDate(patient.dateOfBirth, fmt) : null

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        {/* Intro */}
        {INTRO_TEXT.map((line) => (
          <Text key={line} style={styles.text}>
            {line}
          </Text>
        ))}

        <Spacer height={mm(4)} />

        {/* Q1–3: Name, pets, email */}
        <WithInlineResponse response={<FillField value={fields.fullName || undefined} />}>
          <Text style={styles.text}>
            1. <Text style={styles.bold}>What is your full name?:</Text>
          </Text>
        </WithInlineResponse>
        <Spacer height={mm(2)} />
        <WithInlineResponse response={<FillField value={fields.petNames || undefined} />}>
          <Text style={styles.text}>
            2.{' '}
            <Text style={styles.bold}>
              Please provide the name of the pet(s) you are travelling with:
            </Text>
          </Text>
        </WithInlineResponse>
        <Spacer height={mm(2)} />
        <WithInlineResponse response={<FillField value={fields.emailAddress || undefined} />}>
          <Text style={styles.text}>
            3. <Text style={styles.bold}>Email address:</Text>
          </Text>
        </WithInlineResponse>

        {/* Q4: Travelling person */}
        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            4. Please describe <Text style={styles.bold}>who</Text> will be accompanying the pet(s)
            travelling.{' '}
            <Text style={styles.italic}>
              Please note that the owner or authorised person (who holds a written authorisation
              from the owner) must travel within <Text style={styles.boldItalic}>five</Text> days of
              the animal's movement.
            </Text>
          </Text>
          <Spacer height={mm(2)} />
          <RadioField checked={fields.travellingPerson === 'owner'} label="Owner" />
          <RadioField
            checked={fields.travellingPerson === 'authorised'}
            label="Authorised person"
          />
          <Text style={{ ...styles.text, ...styles.italic, fontSize: 9 }}>
            {AUTHORISED_PERSON_NOTE}
          </Text>
          <Spacer height={mm(2)} />
          <View style={styles.indent}>
            <WithInlineResponse
              response={<FillField value={fields.authorisedRelationship || undefined} />}
            >
              <Text style={styles.text}>
                - Please describe the authorised person's relationship with the owner:
              </Text>
            </WithInlineResponse>
            <Spacer height={mm(2)} />
            <Text style={styles.text}>
              - Will the owner also be joining this trip/ arriving separately within 5 days of
              departure?
            </Text>
            <RadioField checked={fields.ownerJoiningWithin5Days === 'yes'} label="Yes" />
            <RadioField checked={fields.ownerJoiningWithin5Days === 'no'} label="No*" />
          </View>
          <Text style={{ ...styles.text, ...styles.italic, fontSize: 9 }}>
            {OWNER_NOT_JOINING_NOTE}
          </Text>
        </View>

        <SectionDivider />

        {/* TRIP DETAILS */}
        <Text style={styles.sectionTitle}>TRIP DETAILS:</Text>

        <WithInlineResponse response={<FillField value={fields.destination || undefined} />}>
          <Text style={styles.text}>
            1. <Text style={styles.bold}>Destination</Text> of trip:
          </Text>
        </WithInlineResponse>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            1. <Text style={styles.bold}>First EU country</Text> you will enter on your trip?{' '}
            {FIRST_EU_COUNTRY_INFO}
          </Text>
          <Text style={{ ...styles.text, ...styles.italic, fontSize: 9 }}>
            {FIRST_EU_COUNTRY_INFO_ITALIC}
          </Text>
          <Spacer height={mm(2)} />
          {EU_COUNTRIES.map((c) => (
            <RadioField
              key={c.code}
              checked={fields.firstEuCountry === c.code}
              label={formatCountryLabel(c)}
            />
          ))}
        </View>

        <View style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.bold }}>
            How will your pet(s) be getting there?
          </Text>
          {TRAVEL_METHODS.map((m) => (
            <View key={m.value}>
              <RadioField
                checked={fields.travelMethod === m.value}
                label={m.value === 'other' ? 'Other:' : m.label}
              />
              {m.value === 'other' &&
                fields.travelMethod === 'other' &&
                fields.travelMethodOther && (
                  <Text style={{ ...styles.text, ...styles.indent }}>
                    {fields.travelMethodOther}
                  </Text>
                )}
            </View>
          ))}
        </View>

        <View style={styles.sectionGap}>
          <WithInlineResponse
            response={
              <FillField
                value={fields.departureDate ? formatDateNumeric(fields.departureDate) : undefined}
              />
            }
          >
            <Text style={styles.bold}>Date you are departing (dd/mm/yyyy):</Text>
          </WithInlineResponse>
        </View>

        <View style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.bold }}>Date you plan to return:</Text>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: mm(2),
              marginBottom: mm(1.5),
            }}
          >
            <RadioField checked={fields.returnType === 'date'} label="(dd/mm/yyyy):" />
            <FillField
              value={fields.returnDate ? formatDateNumeric(fields.returnDate) : undefined}
            />
          </View>
          <RadioField
            checked={fields.returnType === 'indefinite'}
            label="I plan to move away indefinitely"
          />
        </View>

        <View style={styles.sectionGap}>
          {AHC_VALIDITY_TEXTS.map((text) => (
            <Text key={text.slice(0, 30)} style={{ ...styles.text, ...styles.italic, fontSize: 9 }}>
              {text}
            </Text>
          ))}
          <Spacer height={mm(2)} />
          <Text style={{ ...styles.text, ...styles.italic, fontSize: 9 }}>
            <Text style={styles.boldItalic}>{TAPEWORM_COUNTRIES_NOTE}</Text>
          </Text>
        </View>

        <SectionDivider />

        {/* OWNER DETAILS */}
        <Text style={styles.sectionTitle}>OWNER DETAILS:</Text>

        <WithInlineResponse response={<FillField value={fields.ownerFullName || undefined} />}>
          <Text style={styles.text}>Owner's Full Name:</Text>
        </WithInlineResponse>
        <Spacer height={mm(2)} />
        <WithInlineResponse response={<FillField value={fields.ownerAddress || undefined} />}>
          <Text style={styles.text}>Owner's full UK address:</Text>
        </WithInlineResponse>
        <Spacer height={mm(2)} />
        <WithInlineResponse response={<FillField value={fields.ownerPostcode || undefined} />}>
          <Text style={styles.text}>Owner's UK postcode:</Text>
        </WithInlineResponse>
        <Spacer height={mm(2)} />
        <WithInlineResponse response={<FillField value={fields.ownerPhone || undefined} />}>
          <Text style={styles.text}>Owner's UK mobile phone number:</Text>
        </WithInlineResponse>

        <SectionDivider />

        {/* PET DETAILS */}
        <Text style={styles.sectionTitle}>PET DETAILS:</Text>

        <View wrap={false}>
          <Text style={{ ...styles.text, ...styles.bold }}>
            How many pets are you travelling with?
          </Text>
          {PET_COUNT_OPTIONS.map((n) => (
            <RadioField key={n} checked={fields.petCount === n} label={n} />
          ))}
          <Text style={{ ...styles.text, ...styles.italic, fontSize: 9 }}>{PETS_NOTE}</Text>
        </View>

        <View wrap={false} style={{ marginTop: mm(4) }}>
          <WithInlineResponse response={<FillField value={fields.petName || undefined} />}>
            <Text style={{ ...styles.text, ...styles.bold }}>Please confirm your pet's name:</Text>
          </WithInlineResponse>
        </View>

        <View wrap={false} style={{ marginTop: mm(2) }}>
          <Text style={{ ...styles.text, ...styles.bold }}>Please confirm your pet's sex:</Text>
          {PET_SEX_OPTIONS.map((o) => (
            <RadioField key={o.value} checked={fields.petSex === o.value} label={o.label} />
          ))}
        </View>

        <View wrap={false} style={{ marginTop: mm(2) }}>
          <WithInlineResponse response={<FillField value={fields.petColour || undefined} />}>
            <Text style={{ ...styles.text, ...styles.bold }}>
              Please confirm what colour your pet is:
            </Text>
          </WithInlineResponse>
        </View>

        <View wrap={false} style={{ marginTop: mm(2) }}>
          <WithInlineResponse response={<FillField value={fields.petBreed || undefined} />}>
            <Text style={{ ...styles.text, ...styles.bold }}>
              Please confirm the breed of your pet:
            </Text>
          </WithInlineResponse>
        </View>

        <View wrap={false} style={{ marginTop: mm(2) }}>
          <WithInlineResponse response={<FillField value={fields.microchipNumber || undefined} />}>
            <Text style={{ ...styles.text, ...styles.bold }}>Microchip number:</Text>
          </WithInlineResponse>
          {onFileMicrochip && (
            <Text style={{ ...styles.text, fontSize: 9 }}>
              If we have this number on file, it has been saved as: {onFileMicrochip}.
            </Text>
          )}
        </View>

        <View wrap={false} style={{ marginTop: mm(2) }}>
          <WithInlineResponse response={<FillField value={fields.dateOfBirth || undefined} />}>
            <Text style={{ ...styles.text, ...styles.bold }}>
              Please confirm your pet's date of birth (dd/mm/yyyy):
            </Text>
          </WithInlineResponse>
          {onFileDob && (
            <Text style={{ ...styles.text, fontSize: 9 }}>
              The DOB we have on file is: {onFileDob}.
            </Text>
          )}
        </View>

        <View wrap={false} style={{ marginTop: mm(4) }}>
          <Text style={{ ...styles.text, ...styles.bold }}>
            Is your pet up to date with their rabies vaccinations?
          </Text>
          {RABIES_OPTIONS.map((o) => (
            <RadioField
              key={o.value}
              checked={fields.rabiesVaccination === o.value}
              label={o.label}
            />
          ))}
        </View>

        <View wrap={false} style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.italic, fontSize: 9 }}>{RABIES_PROOF_NOTE}</Text>
          <Spacer height={mm(2)} />
          <Text style={{ ...styles.text, ...styles.italic, fontSize: 9 }}>
            {RABIES_NOT_VACCINATED_NOTE}
          </Text>
        </View>

        <SectionDivider />

        {/* DECLARATION */}
        <Text style={{ ...styles.sectionTitle, ...styles.boldItalic }}>DECLARATION:</Text>

        <CheckboxField checked={fields.declarationAccepted} label={DECLARATION_TEXT} />

        <OwnerSignature owner={context.owner} label="Name" prefillName={false} />

        <SectionDivider />

        <Text style={{ ...styles.text, ...styles.italic }}>
          Thank you for completing this form. Please reach out with any questions.
        </Text>
      </Page>
    </Document>
  )
}
